import { useEffect, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { IconChevronExpandYOutline18 as ChevronsUpDown } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { isLooseGroup } from "@productlane/lib/src/help-center/loose-group"
import { Button, Dialog, Popover } from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"

export type MoveToPortalTarget =
	| {
			kind: "article"
			id: string
			name: string
			groupId: string | null
			portalInstanceId: string | null
	  }
	| {
			kind: "group"
			id: string
			name: string
			articleCount: number
			portalInstanceId: string | null
	  }

const MAIN_PORTAL_NAME = "Main portal"

interface Props {
	target: MoveToPortalTarget
	isOpen: boolean
	onClose: () => void
}

/**
 * Moves an article or a whole group to another portal. An article picks its
 * destination group too, since an article on a different portal than its group
 * shows up on neither.
 */
export function MoveToPortalDialog({ target, isOpen, onClose }: Props) {
	const zero = useZero()
	const workspace = useZeroWorkspace()
	const [groups] = useQuery(queries.helpCenterArticlesWithGroups())

	const [portalId, setPortalId] = useState(target.portalInstanceId)
	const [groupId, setGroupId] = useState(
		target.kind === "article" ? target.groupId : null,
	)
	const [isMoving, setIsMoving] = useState(false)

	const portals = useMemo(
		() => [
			{ id: null as string | null, name: MAIN_PORTAL_NAME },
			...(workspace?.portalInstances ?? []).map((i) => ({
				id: i.id as string | null,
				name: i.name,
			})),
		],
		[workspace?.portalInstances],
	)

	// Top-level pages have no name to pick them by, so the move goes into a group.
	const groupsOnPortal = useMemo(
		() =>
			groups.filter(
				(g) => (g.portalInstanceId ?? null) === portalId && !isLooseGroup(g),
			),
		[groups, portalId],
	)

	useEffect(() => {
		if (target.kind !== "article") return
		if (groupsOnPortal.some((g) => g.id === groupId)) return
		setGroupId(groupsOnPortal[0]?.id ?? null)
	}, [groupsOnPortal, groupId, target.kind])

	const portalName = (id: string | null) =>
		portals.find((p) => p.id === id)?.name ?? MAIN_PORTAL_NAME
	const groupName = (id: string | null) =>
		groups.find((g) => g.id === id)?.name ?? "Pick a group"

	const isUnchanged =
		portalId === target.portalInstanceId &&
		(target.kind !== "article" || groupId === target.groupId)
	const needsGroup = target.kind === "article" && groupId === null
	const canMove = !isUnchanged && !needsGroup && !isMoving

	const close = () => {
		setPortalId(target.portalInstanceId)
		setGroupId(target.kind === "article" ? target.groupId : null)
		onClose()
	}

	const onMove = async () => {
		if (!canMove) return
		setIsMoving(true)
		try {
			if (target.kind === "article" && groupId) {
				await mutateOk(
					zero.mutate(
						mutators.helpCenterArticles.moveToPortal({
							id: target.id,
							helpCenterGroupId: groupId,
							portalInstanceId: portalId,
						}),
					),
				)
			} else if (target.kind === "group") {
				await mutateOk(
					zero.mutate(
						mutators.helpCenterGroups.moveToPortal({
							id: target.id,
							portalInstanceId: portalId,
						}),
					),
				)
			}
			analytics.track("helpcenter-move-to-portal", {
				kind: target.kind,
				id: target.id,
				portalInstanceId: portalId ?? "root",
			})
			toast.success({
				title: `Moved "${target.name}" to ${portalName(portalId)}.`,
			})
			close()
		} catch (error) {
			toast.error({
				title: "Couldn't move this to another portal.",
				description: error instanceof Error ? error.message : undefined,
			})
		} finally {
			setIsMoving(false)
		}
	}

	return (
		<Dialog
			className="max-w-96 p-4"
			isOpen={isOpen}
			onOpenChange={(state) => {
				if (state === false) close()
			}}
		>
			<h2 className="mb-4 text-lg font-bold">{`Move "${target.name}"`}</h2>

			<div className="space-y-3">
				<Field label="Portal">
					<Picker
						label={portalName(portalId)}
						placeholder="Switch portal..."
						empty="No portals."
						options={portals.map((p) => ({
							key: p.id ?? "__root__",
							value: p.name,
							label: p.name,
							onSelect: () => setPortalId(p.id),
						}))}
					/>
				</Field>

				{target.kind === "article" && (
					<Field label="Group">
						<Picker
							label={groupName(groupId)}
							placeholder="Find a group..."
							empty="No groups here yet."
							options={groupsOnPortal.map((g) => ({
								key: g.id,
								value: g.name,
								label: g.name,
								onSelect: () => setGroupId(g.id),
							}))}
						/>
					</Field>
				)}
			</div>

			<p className="mt-3 text-xs text-tertiary">
				{target.kind === "group"
					? `Moves the group and its ${target.articleCount} article${target.articleCount === 1 ? "" : "s"}.`
					: needsGroup
						? `Create a group on ${portalName(portalId)} first, then move the article into it.`
						: "Subpages move with the article."}
			</p>

			<div className="mt-4 flex justify-end gap-2">
				<Button variant="outline" onClick={close}>
					Cancel
				</Button>
				<Button onClick={onMove} disabled={!canMove} isLoading={isMoving}>
					Move
				</Button>
			</div>
		</Dialog>
	)
}

export function Field({
	label,
	children,
}: {
	label: string
	children: React.ReactNode
}) {
	return (
		<label className="flex items-center justify-between gap-3">
			<span className="flex-none text-sm text-secondary">{label}</span>
			{children}
		</label>
	)
}

interface PickerOption {
	key: string
	value: string
	label: string
	onSelect: () => void
}

export function Picker({
	label,
	placeholder,
	empty,
	options,
}: {
	label: string
	placeholder: string
	empty: string
	options: PickerOption[]
}) {
	const [isOpen, setIsOpen] = useState(false)

	return (
		<Popover
			align="end"
			open={isOpen}
			onOpenChange={setIsOpen}
			content={
				<Command>
					<CommandInput placeholder={placeholder} showIcon />
					<CommandList>
						<CommandEmpty>{empty}</CommandEmpty>
						<CommandGroup>
							{options.map((option) => (
								<CommandItem
									key={option.key}
									value={option.value}
									onSelect={() => {
										option.onSelect()
										setIsOpen(false)
									}}
								>
									<span className="truncate text-sm" title={option.label}>
										{option.label}
									</span>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			}
		>
			<button
				type="button"
				className={cn(
					"flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm",
					"hover:bg-gray-50 dark:border-gray-700/80 dark:bg-gray-850 dark:hover:bg-gray-800",
				)}
			>
				<span className="truncate" title={label}>
					{label}
				</span>
				<ChevronsUpDown className="h-3.5 w-3.5 flex-none text-tertiary" />
			</button>
		</Popover>
	)
}
